import React, { useState } from 'react';
import { SendIcon, SmartphoneIcon, CheckCircle2Icon, ClipboardListIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
const CREW_MEMBERS = [{
  name: 'Luis',
  role: 'Rigger'
}, {
  name: 'Jake',
  role: 'Ironworker'
}, {
  name: 'Dan',
  role: 'Crane Operator'
}];
export function Screen18_SendQuiz({
  onNext
}: ScreenProps) {
  const [sending, setSending] = useState(false);
  const [sentMembers, setSentMembers] = useState<Set<string>>(new Set());
  const allSent = sentMembers.size === CREW_MEMBERS.length;
  const handleSend = () => {
    setSending(true);
    CREW_MEMBERS.forEach((member, i) => {
      setTimeout(() => {
        setSentMembers(prev => new Set(prev).add(member.name));
      }, 800 + i * 700);
    });
  };
  return <div className="flex flex-col h-full" data-id="element-887">
      <div className="mb-5" data-id="element-888">
        <h2 className="text-2xl font-bold text-white mb-2" data-id="element-889">
          Crew Understanding Quiz
        </h2>
        <p className="text-slate-400 text-sm" data-id="element-890">
          Send a short safety quiz to verify each crew member understands the
          PTP before work begins.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-5" data-id="element-891">
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-3" data-id="element-892">
          <div className="flex items-center gap-3" data-id="element-893">
            <ClipboardListIcon className="w-5 h-5 text-yellow-500 flex-shrink-0" data-id="element-894" />
            <div data-id="element-895">
              <p className="text-white font-semibold text-sm" data-id="element-896">
                PTP Safety Quiz
              </p>
              <p className="text-slate-400 text-xs" data-id="element-897">
                3 questions · Based on today's PTP
              </p>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-3 space-y-2" data-id="element-898">
            <div className="flex items-center justify-between" data-id="element-899">
              <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold" data-id="element-900">
                Task
              </span>
              <span className="text-yellow-400 text-xs font-bold" data-id="element-901">
                Steel Beam Installation
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-902">
              <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold" data-id="element-903">
                Questions
              </span>
              <span className="text-white text-xs font-medium" data-id="element-904">
                Hazards, controls, emergency response
              </span>
            </div>
          </div>
        </div>

        <section data-id="element-905">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-906">
            Send Quiz To
          </h3>
          <div className="space-y-2" data-id="element-907">
            {CREW_MEMBERS.map(member => <div key={member.name} className="bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 flex items-center gap-3" data-id="element-908">
                <SmartphoneIcon className="w-4 h-4 text-slate-500 flex-shrink-0" data-id="element-909" />
                <div className="flex-1" data-id="element-910">
                  <span className="text-white font-medium text-sm" data-id="element-911">
                    {member.name}
                  </span>
                  <span className="text-slate-500 text-xs ml-2" data-id="element-912">
                    · {member.role}
                  </span>
                </div>
                <AnimatePresence mode="wait" data-id="element-913">
                  {sentMembers.has(member.name) ? <motion.div key="sent" initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 400,
                damping: 15
              }} data-id="element-914">
                      <CheckCircle2Icon className="w-5 h-5 text-green-500" data-id="element-915" />
                    </motion.div> : sending ? <motion.div key="sending" animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-5 h-5 rounded-full border-2 border-slate-700 border-t-yellow-500" data-id="element-916" /> : <motion.div key="pending" className="w-2 h-2 rounded-full bg-slate-600" data-id="element-917" />}
                </AnimatePresence>
              </div>)}
          </div>
        </section>

        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4" data-id="element-918">
          <p className="text-yellow-100 text-sm leading-relaxed" data-id="element-919">
            <span className="font-bold text-white" data-id="element-920">Note:</span> After sending
            the quiz to the crew, you will also complete the quiz yourself as
            the foreman.
          </p>
        </div>

        <AnimatePresence data-id="element-921">
          {allSent && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3,
          duration: 0.3
        }} className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3" data-id="element-922">
              <CheckCircle2Icon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-923" />
              <div data-id="element-924">
                <p className="text-green-300 font-semibold text-sm" data-id="element-925">
                  Quiz sent to {CREW_MEMBERS.length} crew members
                </p>
                <p className="text-green-200/60 text-xs mt-0.5" data-id="element-926">
                  Workers will receive the quiz on their devices.
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      <div className="pt-4 space-y-3 bg-slate-950" data-id="element-927">
        {!sending ? <PrimaryButton onClick={handleSend} icon={<SendIcon className="w-5 h-5" data-id="element-929" />} data-id="element-928">
            Send Quiz to Crew
          </PrimaryButton> : <PrimaryButton onClick={onNext} disabled={!allSent} icon={<ClipboardListIcon className="w-5 h-5" data-id="element-931" />} data-id="element-930">
            {allSent ? 'Take Quiz as Foreman' : `Sending quiz… (${sentMembers.size}/${CREW_MEMBERS.length})`}
          </PrimaryButton>}
      </div>
    </div>;
}